import { useEffect, useState } from 'react';
import { Cradle, PlatformStats } from '@solo/app-core';
import { DigitalDistributionIcons, Flex, InputTextAutocomplete, Text } from '@/app/ui';
import { translate } from '@/app/i18n';
import ProgressBar from '@/app/ui/atoms/ProgressBar';
import Colors from '@/app/styles/Colors';
import { spanFilters, TimeSpan } from '@/app/utils/GetDateFrom';
import { useCradle } from '@/app/contexts';

interface PlaysByPlatformProps {
  top4?: boolean;
}

const PlaysByPlatform = ({ top4 = false }: PlaysByPlatformProps) => {
  const { statisticsBloc } = useCradle<Cradle>();
  const [topPlatforms, setTopPlatforms] = useState<PlatformStats[]>();

  async function getPlays(filter: TimeSpan = TimeSpan.lastYear) {
    let period: 'last_month' | 'this_year' | 'last_year' = 'last_year';
    if (filter === TimeSpan.lastMonth) {
      period = 'last_month';
    } else if (filter === TimeSpan.lastYear) {
      period = 'last_year';
    } else if (filter === TimeSpan.thisYear) {
      period = 'this_year';
    }
    const platforms = await statisticsBloc.getPlatforms({
      offset: 0,
      period,
    });
    if (top4) {
      setTopPlatforms(platforms.sort((a, b) => b.plays - a.plays).slice(0, 4));
    } else {
      setTopPlatforms(platforms);
    }
  }

  useEffect(() => {
    getPlays();
  }, []);

  return (
    <Flex.Container className="mt-5" flexDirection="column" gap="4rem">
      {!top4 && (
        <Flex.Container style={{ width: '20%' }}>
          <InputTextAutocomplete
            items={spanFilters}
            onChange={(filter) => getPlays(filter as TimeSpan)}
          />
        </Flex.Container>
      )}
      <Flex.Container flexWrap="wrap" gap="1rem 3rem">
        {topPlatforms?.map((platform) => (
          <Flex.Container
            gap="1rem"
            key={platform.platform}
            alignItems="center"
            padding="0 0 0.5rem"
            style={{ width: '48%', borderBottom: `1px solid ${Colors.borderInput}` }}
          >
            <Flex.Container gap="1rem">
              <DigitalDistributionIcons
                icon={platform.platform.toLowerCase().replace(' ', '-')}
                width={24}
              />
              <Text.LabelMedium style={{ paddingTop: '3px' }}>{platform.platform}</Text.LabelMedium>
            </Flex.Container>
            <Flex.Container
              className="mb-2"
              style={{ width: '50%', marginLeft: 'auto' }}
              alignItems="flex-end"
              flexDirection="column"
            >
              <Text.Anotation>
                {platform.plays} {translate('USAGES.PLAYS_LOWER_CASE')}
              </Text.Anotation>
              <ProgressBar
                progress={(platform.plays / Math.max(...topPlatforms.map((p) => p.plays))) * 100}
              />
            </Flex.Container>
          </Flex.Container>
        ))}
        {topPlatforms?.length === 0 && (
          <Text.Anotation>{translate('USAGES.NO_DATA')}</Text.Anotation>
        )}
      </Flex.Container>
    </Flex.Container>
  );
};

export default PlaysByPlatform;
